import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getIdToken,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  // onIdTokenChanged,
  signOut,
  updatePassword as firebaseUpdatePassword,
  // sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  onIdTokenChanged
} from 'firebase/auth'
import type { Auth, User as FirebaseUser } from 'firebase/auth'
import { collection, doc, onSnapshot } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'

import { defineStore } from 'pinia'

export type AuthStates = 'init' | 'authed' | 'not-authed'

/**
 * The status of fetching the user doc
 *
 * init = the app has initialised and the user hasn't been authed yet
 * not-authed = the user is not authenticated
 * not-fetching = the user is authed and the user doc is not being fetched becuase no userCollection is set in app.config.ts
 * fetching = the user is authed and the user doc is being fetched
 * fetched = the user is authed and the user doc has been fetched and has a value
 * no-value = the user is authed and the user doc has been fetched but has no value
 */
export type UserDocStatuses = 'init' | 'not-authed' | 'not-fetching' | 'fetching' | 'fetched' | 'no-value'

export const useAuthStore = defineStore('auth', () => {
  const _userType = useAppConfig().hiyieldNuxtLayerFirebaseAuth.userDocType
  type User = typeof _userType
  const { userCollection } = useAppConfig().hiyieldNuxtLayerFirebaseAuth

  const authState = ref<AuthStates>('init')

  const idToken = ref<string | undefined>('')
  const firebaseUser = ref<FirebaseUser | undefined>(undefined)

  const userDoc = ref<User | undefined>()
  const userDocStatus = ref<UserDocStatuses>('init')
  const userDocLoading = computed(() => {
    const s = userDocStatus.value
    return s === 'init' || s === 'fetching'
  })

  const resetState = () => {
    authState.value = 'not-authed'
    userDocStatus.value = 'not-authed'
    firebaseUser.value = undefined
    userDoc.value = undefined
    idToken.value = undefined
  }

  const setAuthedState = async (authUser: FirebaseUser) => {
    firebaseUser.value = authUser
    idToken.value = await getIdToken(authUser)
    authState.value = 'authed'
  }

  /**
   * Sign up
   */
  const signingUp = ref(false)
  const signUp = async (email: string, password: string, userData?: Partial<User>) => {
    try {
      // get auth
      const auth = useFirebaseAuth() as Auth
      if (auth.currentUser) await logOut()

      signingUp.value = true

      // create the user
      const { user } = await createUserWithEmailAndPassword(auth, email, password)

      // set authed state
      await setAuthedState(user)

      if (userCollection) {
        const newUserDoc = await $authedFetch().fetch('/api/user/update', {
          method: 'POST',
          body: userData
        })
        userDoc.value = newUserDoc
      }

      signingUp.value = false
    } catch (e) {
      signingUp.value = false
      throw e
    }
  }

  /**
   * Update/create user doc
   */
  const updatingUserDoc = ref(false)
  const updateUserDoc = async (userInfo: Partial<User>) => {
    try {
      if (!userCollection) {
        throw new Error(
          'No userCollection found in app.config.ts under hiyieldNuxtLayerFirebaseAuth.userCollection'
        )
      }

      updatingUserDoc.value = true
      await $authedFetch().fetch(`/user/update`, {
        method: 'POST',
        body: userInfo
      })

      updatingUserDoc.value = false
    } catch (e) {
      updatingUserDoc.value = false
      throw e
    }
  }

  /**
   * Log in
   */
  const loggingIn = ref(false)
  const logIn = async (email: string, password: string) => {
    try {
      const auth = useFirebaseAuth() as Auth
      loggingIn.value = true

      // logout if already logged in
      if (auth.currentUser) await logOut()

      // log in
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      await setAuthedState(user)

      loggingIn.value = false
    } catch (e) {
      loggingIn.value = false
      throw e
    }
  }

  /**
   * Log out
   */
  const loggingOut = ref(false)
  const logOut = async () => {
    try {
      // stop the user doc snapshot listener
      if (userDocumentWatcher) userDocumentWatcher()
      const auth = useFirebaseAuth() as Auth
      loggingOut.value = true
      await signOut(auth)
      resetState()
      loggingOut.value = false
    } catch (e) {
      loggingOut.value = false
      throw e
    }
  }

  /**
   * Update password
   */
  const updatingPassword = ref(false)
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      updatingPassword.value = true

      const auth = useFirebaseAuth() as Auth
      const user = auth.currentUser

      if (!user) throw new Error('No user found')
      if (!user.email) throw new Error('No user email found')
      if (!currentPassword) throw new Error('No current password provided')
      if (!newPassword) throw new Error('No new password provided')

      // reauthenticate to make sure the user is who they say they are
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword))

      // update the password
      await firebaseUpdatePassword(user, newPassword)
      updatingPassword.value = false
    } catch (e) {
      updatingPassword.value = false
      throw e
    }
  }

  // somewhere to keep watcher for userDocument
  let userDocumentWatcher: () => void

  // setup watcher for authed state
  onMounted(() => {
    onIdTokenChanged(useFirebaseAuth() as Auth, async (user) => {
      if (user) {
        await setAuthedState(user)
        if (userCollection) {
          userDocStatus.value = 'fetching'
          const userCollectionRef = collection(useFirestore() as Firestore, userCollection)
          // this is an any type because we don't know the shape of the user doc
          const userDocRef = doc<any>(userCollectionRef, user.uid)
          userDocumentWatcher = onSnapshot(userDocRef, (snap) => {
            const doc = snap?.data()
            if (doc) {
              userDoc.value = doc
              userDocStatus.value = 'fetched'
            } else {
              userDocStatus.value = 'no-value'
            }
          })
        } else {
          userDocStatus.value = 'not-fetching'
        }
      } else {
        // unwatch the user doc
        if (userDocumentWatcher) userDocumentWatcher()
        resetState()
      }
    })
  })

  return {
    authState,
    firebaseUser,
    idToken,

    logIn,
    loggingIn,

    signUp,
    signingUp,

    logOut,
    loggingOut,

    updatePassword,
    updatingPassword,

    userDoc,
    updateUserDoc,
    updatingUserDoc,
    userDocStatus,
    userDocLoading,

    setAuthedState,
    resetState
  }
})
